import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Login.css';
import AuthCard from '../AuthCard/AuthCard';
import { useAuth } from '../../contexts/AuthContext';
import { CONFIRM_SIGN_UP_REQUIRED, NEW_PASSWORD_REQUIRED, PASSWORD_RESET_REQUIRED } from '../../constants';
import ConfirmSignupForm from '../Signup/ConfirmSignupForm';
import { getReturnPath } from '../../utils/returnPath';

interface LoginProps {
  onLogin: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const { needsNewPassword, setNewPassword, isAuthenticated, initiatePasswordReset, completePasswordReset, resendRegistrationCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Where ProtectedRoute sent the user from, if anywhere (same-origin only).
  const returnPath = getReturnPath(location.state);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [newPasswordData, setNewPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [notice, setNotice] = useState<{ kind: 'success' | 'info'; text: string } | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [resetStep, setResetStep] = useState<'email' | 'code' | null>(null);
  const [resetEmail, setResetEmail] = useState('');
  const [resetData, setResetData] = useState({
    code: '',
    newPassword: '',
    confirmPassword: ''
  });
  // Set when sign-in reports an unconfirmed account. The email and password stay
  // in formData so confirming can sign straight in without asking again.
  const [confirmEmail, setConfirmEmail] = useState<string | null>(null);
  // Bumped to remount ConfirmSignupForm, which clears its code field.
  const [confirmAttempt, setConfirmAttempt] = useState(0);

  // Redirect if already authenticated (after password change). Same
  // destination as App's handleLogin, so the two never disagree.
  useEffect(() => {
    if (isAuthenticated && !needsNewPassword) {
      navigate(returnPath);
    }
  }, [isAuthenticated, needsNewPassword, navigate, returnPath]);

  // Support reset this account's password (admin-reset-user-password), which
  // has already emailed a code. Go straight to the code step for the email the
  // user signed in with; calling initiatePasswordReset here would send a second code.
  const startRequiredPasswordReset = () => {
    setConfirmEmail(null);
    setResetEmail(formData.email);
    setResetData({ code: '', newPassword: '', confirmPassword: '' });
    setResetStep('code');
    setNotice({ kind: 'info', text: 'Your password needs to be reset. Enter the code we emailed you and choose a new password.' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setNotice(null);

    try {
      await onLogin(formData.email, formData.password, rememberMe);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed. Please check your credentials.';
      console.error('Login failed:', message);
      if (message === CONFIRM_SIGN_UP_REQUIRED) {
        setConfirmEmail(formData.email);
        try {
          await resendRegistrationCode(formData.email);
          setNotice({ kind: 'info', text: "Your email isn't verified yet. We've sent you a new code." });
        } catch (resendError) {
          setError(resendError instanceof Error ? resendError.message : 'Unable to send a new code.');
        }
      } else if (message === PASSWORD_RESET_REQUIRED) {
        startRequiredPasswordReset();
      } else if (message !== NEW_PASSWORD_REQUIRED) {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // The outcome is always 'CONFIRMED' here: the signIn that reported the
  // account unconfirmed also reset Amplify's auto sign-in state. So sign in
  // again with the credentials the user already typed. App navigates on success.
  const handleSignupConfirmed = async () => {
    setError('');
    setNotice(null);
    try {
      await onLogin(formData.email, formData.password, rememberMe);
    } catch (loginError) {
      const message = loginError instanceof Error ? loginError.message : '';
      if (message === CONFIRM_SIGN_UP_REQUIRED) {
        // The confirmation did not take (a trigger timeout, say). Stay on the
        // confirm step; no second automatic resend.
        setConfirmAttempt(n => n + 1);
        setNotice({ kind: 'info', text: "We couldn't finish verifying your email. Enter the code again, or request a new one." });
      } else if (message === PASSWORD_RESET_REQUIRED) {
        startRequiredPasswordReset();
      } else {
        // Never show the raw message: it may be a Cognito string or a sentinel.
        setConfirmEmail(null);
        setFormData(prev => ({ ...prev, password: '' }));
        setNotice({ kind: 'info', text: 'Your email is verified. Sign in with your password.' });
      }
    }
  };

  const handleCancelConfirm = () => {
    setConfirmEmail(null);
    setNotice(null);
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPasswordData.newPassword !== newPasswordData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPasswordData.newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    setError('');
    setNotice(null);

    try {
      if (setNewPassword) {
        await setNewPassword(newPasswordData.newPassword);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to set new password';
      console.error('Set password failed:', message);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleForgotPasswordClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setError('');
    setNotice(null);
    setResetStep('email');
    setResetEmail(formData.email); // Pre-fill if user already entered email
  };

  const handleResetEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice(null);
    setIsLoading(true);

    try {
      await initiatePasswordReset(resetEmail);
      setResetStep('code');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to send reset code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice(null);

    // Client-side validation
    if (resetData.newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (resetData.newPassword !== resetData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await completePasswordReset(resetEmail, resetData.code, resetData.newPassword);
      // Success - return to login
      setResetStep(null);
      setResetEmail('');
      setResetData({ code: '', newPassword: '', confirmPassword: '' });
      setError('');
      setNotice({ kind: 'success', text: 'Password reset successful! Please sign in with your new password.' });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Password reset failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setNotice(null);
    setIsLoading(true);

    try {
      await initiatePasswordReset(resetEmail);
      setNotice({ kind: 'info', text: 'A new verification code has been sent to your email.' });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelReset = () => {
    setResetStep(null);
    setResetEmail('');
    setResetData({ code: '', newPassword: '', confirmPassword: '' });
    setError('');
    setNotice(null);
  };

  const title =
    confirmEmail ? 'Verify your email' :
    needsNewPassword ? 'Set New Password' :
    resetStep === 'email' ? 'Reset Password' :
    resetStep === 'code' ? 'Enter Verification Code' :
    'Welcome to PropWise';
  const subtitle =
    confirmEmail ? `We've sent a code to ${confirmEmail}` :
    needsNewPassword ? 'Please set a new password for your account' :
    resetStep === 'email' ? 'Enter your email address to receive a verification code' :
    resetStep === 'code' ? `We've sent a code to ${resetEmail}` :
    'Sign in to access your property inspection dashboard';

  return (
    <AuthCard title={title} subtitle={subtitle}>
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {notice && (
        <div className={`alert alert-${notice.kind}`} role="status">
          {notice.text}
        </div>
      )}

      {confirmEmail ? (
        <ConfirmSignupForm
          key={confirmAttempt}
          email={confirmEmail}
          onConfirmed={handleSignupConfirmed}
          onCancel={handleCancelConfirm}
        />
      ) : needsNewPassword ? (
        <form onSubmit={handleNewPasswordSubmit} className="login-form">
          <div className="form-group">
            <div className="form-floating">
              <input
                type="password"
                className="form-control"
                id="newPassword"
                placeholder="New Password"
                value={newPasswordData.newPassword}
                onChange={(e) => setNewPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                required
                minLength={8}
              />
              <label htmlFor="newPassword">New Password (min 8 characters)</label>
            </div>
          </div>

          <div className="form-group">
            <div className="form-floating">
              <input
                type="password"
                className="form-control"
                id="confirmPassword"
                placeholder="Confirm Password"
                value={newPasswordData.confirmPassword}
                onChange={(e) => setNewPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                required
              />
              <label htmlFor="confirmPassword">Confirm Password</label>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-login"
            disabled={isLoading || !newPasswordData.newPassword || !newPasswordData.confirmPassword}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Setting Password...
              </>
            ) : (
              'Set New Password'
            )}
          </button>
        </form>
      ) : resetStep === 'email' ? (
        <form onSubmit={handleResetEmailSubmit} className="login-form">
          <div className="form-group">
            <div className="form-floating">
              <input
                type="email"
                className="form-control"
                id="resetEmail"
                placeholder="name@example.com"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
              />
              <label htmlFor="resetEmail">Email address</label>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-login"
            disabled={isLoading || !resetEmail}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Sending Code...
              </>
            ) : (
              'Send Verification Code'
            )}
          </button>

          <button
            type="button"
            className="btn btn-link btn-cancel"
            onClick={handleCancelReset}
          >
            Back to Sign In
          </button>
        </form>
      ) : resetStep === 'code' ? (
        <form onSubmit={handleResetCodeSubmit} className="login-form">
          <div className="form-group">
            <div className="form-floating">
              <input
                type="text"
                className="form-control"
                id="resetCode"
                placeholder="123456"
                value={resetData.code}
                onChange={(e) => setResetData(prev => ({ ...prev, code: e.target.value }))}
                required
                autoComplete="one-time-code"
              />
              <label htmlFor="resetCode">Verification Code</label>
            </div>
          </div>

          <div className="form-group">
            <div className="form-floating">
              <input
                type="password"
                className="form-control"
                id="resetNewPassword"
                placeholder="New Password"
                value={resetData.newPassword}
                onChange={(e) => setResetData(prev => ({ ...prev, newPassword: e.target.value }))}
                required
                minLength={8}
              />
              <label htmlFor="resetNewPassword">New Password (min 8 characters)</label>
            </div>
          </div>

          <div className="form-group">
            <div className="form-floating">
              <input
                type="password"
                className="form-control"
                id="resetConfirmPassword"
                placeholder="Confirm Password"
                value={resetData.confirmPassword}
                onChange={(e) => setResetData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                required
              />
              <label htmlFor="resetConfirmPassword">Confirm Password</label>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-login"
            disabled={isLoading || !resetData.code || !resetData.newPassword || !resetData.confirmPassword}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Resetting Password...
              </>
            ) : (
              'Reset Password'
            )}
          </button>

          <div className="reset-actions">
            <button
              type="button"
              className="btn btn-link"
              onClick={handleResendCode}
              disabled={isLoading}
            >
              Resend Code
            </button>
            <button
              type="button"
              className="btn btn-link btn-cancel"
              onClick={handleCancelReset}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <div className="form-floating">
              <input
                type="email"
                className="form-control"
                id="email"
                name="email"
                placeholder="name@example.com"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <label htmlFor="email">Email address</label>
            </div>
          </div>

          <div className="form-group">
            <div className="form-floating">
              <input
                type="password"
                className="form-control"
                id="password"
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <label htmlFor="password">Password</label>
            </div>
          </div>

          <div className="form-group">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="rememberMe">
                Remember me
              </label>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-login"
            disabled={isLoading || !formData.email || !formData.password}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Signing in...
              </>
            ) : (
              <>
                <i className="bi bi-box-arrow-in-right me-2"></i>
                Sign In
              </>
            )}
          </button>
        </form>
      )}

      {!resetStep && !needsNewPassword && !confirmEmail && (
        <div className="login-footer">
          <div className="login-links">
            <Link to="/signup" className="login-link">Create an account</Link>
            <button
              type="button"
              className="login-link"
              onClick={handleForgotPasswordClick}
            >
              Forgot your password?
            </button>
          </div>
        </div>
      )}
    </AuthCard>
  );
};

export default Login;
